import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import { ReactNode } from "react";
import { BacktestingStatisticCard } from "features/bots/BotForm/common/styles";
import useMedia from "utils/hooks/useMedia";
import { MOBILE_SEARCH } from "common/constants";
import { typography } from "@shared/common/Themes/typography";
import {
  HelperTooltip,
  HelperTooltipProps,
} from "common/Tooltips/HelperTooltip";

interface StatisticCardProps {
  title: string;
  value?: number | string | ReactNode;
  extraComponent?: ReactNode;
  helperProps?: HelperTooltipProps;
  gridArea: string;
  /** New look: a small chart on the right of the figures. */
  aside?: ReactNode;
}

const StatisticCard = ({
  title,
  value,
  extraComponent,
  helperProps,
  gridArea,
  aside,
}: StatisticCardProps) => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { isNewLook } = useTheme();

  if (isNewLook) {
    const figures = (
      <>
        <NewLookLabel>
          {title}
          {helperProps && <HelperTooltip {...helperProps} />}
        </NewLookLabel>
        <NewLookValue>{value}</NewLookValue>
        {extraComponent && <NewLookExtra>{extraComponent}</NewLookExtra>}
      </>
    );
    return (
      <NewLookTile style={{ gridArea }}>
        {aside ? (
          <NewLookRow>
            <div style={{ minWidth: 0 }}>{figures}</div>
            <NewLookAside>{aside}</NewLookAside>
          </NewLookRow>
        ) : (
          figures
        )}
      </NewLookTile>
    );
  }

  return (
    <BacktestingStatisticCard
      style={{ gridArea, height: "100%" }}
      $isMobile={isMobile}
    >
      <LeftBorder>
        <ContentWrapper $extraComponent={!!extraComponent}>
          <Text
            as="div"
            type="secondary"
            $size={isMobile ? typography.fontSizes.S : typography.fontSizes.M}
          >
            {title}
            {helperProps && <HelperTooltip {...helperProps} />}
          </Text>
          <StyledTitle level={isMobile ? 4 : 3}>{value}</StyledTitle>
          {extraComponent}
        </ContentWrapper>
      </LeftBorder>
    </BacktestingStatisticCard>
  );
};

export default StatisticCard;

const LeftBorder = styled.div`
  border-left: 6px solid ${(p) => p.theme.colors.midBlue};
  padding-left: 12px;
  height: 100%;
`;
const ContentWrapper = styled.div<{ $extraComponent?: boolean }>`
  padding: ${(p) => (p.$extraComponent ? 0 : "12px 0")};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const StyledTitle = styled(Title)`
  white-space: nowrap;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;

/* New look: a quiet tile — muted label, one clear figure, details below. */
export const NewLookTile = styled.div`
  min-width: 0;
  padding: 14px 16px;
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  border-radius: 12px;
  background: ${(p) => p.theme.colors.cardBgColor};
`;
export const NewLookLabel = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 12.5px;
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
`;
const NewLookValue = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 16px;
  line-height: 1.35;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-variant-numeric: tabular-nums;
`;
const NewLookRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;
const NewLookAside = styled.div`
  flex: none;
`;
const NewLookExtra = styled.div`
  margin-top: 4px;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 13px;
  .ant-typography {
    color: inherit;
    font-size: inherit;
  }
`;
